import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

function cancelScheduledIdleCallback(callbackId) {
  if (typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(callbackId);
  } else {
    window.clearTimeout(callbackId);
  }
}

export class FloatingPanelComponent extends Component {

  #idleCallbackId = null;

  #connectionEpoch = 0;

  #updatePosition = async () => {
    const connectionEpoch = this.#connectionEpoch;
    if (!this.isConnected) return;

    if (viewTransition.current) await viewTransition.current;
    if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    const connectionEpoch = ++this.#connectionEpoch;
    this.#cancelIdleCallback();
    window.addEventListener('resize', this.#resizeListener);

    const idleCallbackId = requestIdleCallback(() => {
      if (this.#idleCallbackId === idleCallbackId) this.#idleCallbackId = null;
      if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
    this.#idleCallbackId = idleCallbackId;
  }

  disconnectedCallback() {
    this.#connectionEpoch += 1;
    this.#cancelIdleCallback();
    this.#resizeListener.cancel();
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }

  #cancelIdleCallback() {
    if (this.#idleCallbackId === null) return;

    cancelScheduledIdleCallback(this.#idleCallbackId);
    this.#idleCallbackId = null;
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
